
import { TriangleDownIcon, XCircleFillIcon } from '@primer/octicons-react';
import { Box, Button, SelectPanel } from '@primer/react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadNhomKhoanNopStart } from '../../state/actions/nhomKhoanNopActions';
import { RootState } from '../../state/reducers';

type SelectBoxMultipleKhomKhoanNopProps = {
    isReadonly?: boolean;
    value?: number[];
    onValueChanged: (ids: number[]) => void;
    className?: string;
    preText?: string;
    width?: string | number;
    stylingMode?: 'outlined' | 'filled' | 'underlined';
    isShowClearBtn?: boolean;
    maxWidth?: any;
    size?: "small" | "medium" | "large";
};

const SelectBoxMultipleKhomKhoanNop = (props: SelectBoxMultipleKhomKhoanNopProps) => {
    const [filter, setFilter] = useState('');
    const [open, setOpen] = useState(false);
    const { dm_coso_id } = useSelector((state: RootState) => state.common);
    const { sf_nhomkhoannops } = useSelector((state: RootState) => state.nhomKhoanNop);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(loadNhomKhoanNopStart());
    }, [dispatch]);

    const dataSource = useMemo(() => {
        let data = sf_nhomkhoannops.filter(x => x.dm_coso_id === dm_coso_id);
        data = [...data].sort((a: any, b: any) => {
            const valA = (a.thutu_sapxep ?? "").toString();
            const valB = (b.thutu_sapxep ?? "").toString();
            if (valA && valB) {
                const cmp = valA.localeCompare(valB, undefined, { numeric: true });
                if (cmp !== 0) return cmp;
            }
            if (valA && !valB) return -1;
            if (!valA && valB) return 1;
            return (a.nhom_khoan_nop || "").localeCompare(b.nhom_khoan_nop || "");
        });
        return data.map(x => ({ id: x.id, text: `${x.ma_nhom} - ${x.nhom_khoan_nop}` }));
    }, [sf_nhomkhoannops, dm_coso_id]);

    const removeVietnameseTones = (str: string) => {
        return str
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .replace(/đ/g, 'd')
            .replace(/Đ/g, 'D');
    };

    const filteredData = useMemo(() => {
        const normalizedFilter = removeVietnameseTones(filter.toLowerCase());
        return dataSource.filter(item => removeVietnameseTones(item.text.toLowerCase()).includes(normalizedFilter));
    }, [dataSource, filter]);

    const _selectedData = useMemo(() => {
        return dataSource.filter(item => props.value?.includes(item.id));
    }, [props.value, dataSource]);

    const anchorLabel = useMemo(() => {
        const count = _selectedData.length;
        if (count === 0) return 'Chọn nhóm khoản nộp';
        if (count <= 2) return _selectedData.map(x => x.text).join(', ');
        return `${count} items selected`;
    }, [_selectedData]);

    return (
        <SelectPanel
            renderAnchor={({ 'aria-labelledby': ariaLabelledBy, ...anchorProps }) => (
                <Button
                    sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', fontSize: props.size === "small" ? "12px" : "14px" }}
                    size={props.size}
                    trailingAction={TriangleDownIcon}
                    aria-labelledby={ariaLabelledBy}
                    {...anchorProps}
                >
                    <p style={{ maxWidth: props.maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: props.size === "small" ? "12px" : "14px" }}>
                        {anchorLabel}
                    </p>
                </Button>
            )}

            title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>Chọn nhóm khoản nộp</Box>
                    {props.isShowClearBtn && (props.value?.length ?? 0) > 0 && (
                        <Button
                            trailingVisual={XCircleFillIcon}
                            variant='invisible'
                            sx={{ color: 'danger.emphasis' }}
                            onClick={() => props.onValueChanged([])}
                        >
                            Bỏ chọn
                        </Button>
                    )}
                </Box>
            }
            placeholderText='Search'
            open={open}
            onOpenChange={setOpen}
            

            items={filteredData}
            selected={_selectedData}
            onSelectedChange={(selectedItems: any[]) => {
                const newValues = selectedItems.map(item => item.id);
                props.onValueChanged(newValues);
            }}
            onFilterChange={setFilter}
            showItemDividers={true}
            overlayProps={{ width: 'large', height: 'medium' }}
        />
    );
};

export default SelectBoxMultipleKhomKhoanNop;
